import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
    Users, TrendingUp, Globe, Briefcase,
    ChevronRight, Award, Clock, ArrowUpRight,
    Activity, RefreshCw, AlertCircle,
    Settings, X, Info, MapPin, Fingerprint,
    Zap, Coffee, MessageSquare, Code, Download,
    PieChart, BarChart2, Search, Layout, Plus,
    FileText, User, Bell, HelpCircle, Trash2, Edit3,
    Shield, HardDrive, Lock, Calendar, CheckCircle, ChevronLeft, ChevronRight as ChevronR,
    Play, RotateCcw, ChevronDown
} from "lucide-react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/api";
import { displayName } from "../../utils/displayName";
import { toast } from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import socket from "../../utils/socket";
import Skeleton from "../common/Skeleton";
import { PillSelect } from "../common/ui.jsx";
import "./analytics.css";

const MODAL_LIST_PAGE_SIZE = 10;

const toYmd = (dateObj) => dateObj.toISOString().split("T")[0];

// Plain toLocaleString() renders a slash-separated numeric date (e.g. "9/7/2026")
// whose month/day order silently flips between locales (US: M/D, most others: D/M) —
// the same string can be misread as two different dates. Spelling out the month
// removes the ambiguity regardless of the viewer's locale.
const formatDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit",
    });
};

const dateToIsoWeek = (date) => {
    const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = utcDate.getUTCDay() || 7;
    utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((utcDate - yearStart) / 86400000) + 1) / 7);
    return `${utcDate.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
};

const getCurrentIsoWeek = () => dateToIsoWeek(new Date());

const getWeekRange = (weekValue) => {
    const match = /^(\d{4})-W(\d{2})$/.exec(String(weekValue || ""));
    if (!match) return null;
    const year = Number(match[1]);
    const week = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(week) || week < 1 || week > 53) return null;

    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Day = jan4.getUTCDay() || 7;
    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1);

    const start = new Date(week1Monday);
    start.setUTCDate(week1Monday.getUTCDate() + ((week - 1) * 7));
    const end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);

    return { start: toYmd(start), end: toYmd(end) };
};

const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const getMonthRange = (monthValue) => {
    const match = /^(\d{4})-(\d{2})$/.exec(String(monthValue || ""));
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;

    const start = new Date(Date.UTC(year, month - 1, 1));
    const end = new Date(Date.UTC(year, month, 0));
    return { start: toYmd(start), end: toYmd(end) };
};

const getYearRange = (yearValue) => {
    const year = Number(yearValue);
    if (!Number.isFinite(year)) return null;
    const start = new Date(Date.UTC(year, 0, 1));
    const end = new Date(Date.UTC(year, 11, 31));
    return { start: toYmd(start), end: toYmd(end) };
};

// Native <input type="week"|"month"> render as an uninteractive raw-text box
// in browsers without a built-in picker (Firefox in particular) — these
// pickers replace them with plain <select> dropdowns instead.
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

const parseMonthValue = (value) => {
    const match = /^(\d{4})-(\d{2})$/.exec(String(value || ""));
    return match ? { year: Number(match[1]), month: Number(match[2]) } : { year: new Date().getFullYear(), month: 1 };
};

const parseWeekValue = (value) => {
    const match = /^(\d{4})-W(\d{2})$/.exec(String(value || ""));
    return match ? { year: Number(match[1]), week: Number(match[2]) } : { year: new Date().getFullYear(), week: 1 };
};

// Matches the Attendance heatmap's own week-view label exactly (see
// EmployeeAnalytics.jsx's formatWeekRange) — a prev/next stepper over a
// date-range label reads far more like an actual calendar than picking a
// raw week number ever did.
const formatWeekRangeLabel = (weekValue) => {
    const range = getWeekRange(weekValue);
    if (!range) return "";
    const ws = new Date(`${range.start}T00:00:00`);
    const we = new Date(`${range.end}T00:00:00`);
    const opts = { month: "short", day: "numeric" };
    const sameYear = ws.getFullYear() === we.getFullYear();
    const start = ws.toLocaleDateString("en-US", opts);
    const end = we.toLocaleDateString("en-US", sameYear ? opts : { ...opts, year: "numeric" });
    return `${start} – ${end}${sameYear ? `, ${ws.getFullYear()}` : ""}`;
};

const stepIsoWeek = (weekValue, dir) => {
    const range = getWeekRange(weekValue);
    const base = range ? new Date(`${range.start}T00:00:00`) : new Date();
    base.setDate(base.getDate() + dir * 7);
    return dateToIsoWeek(base);
};

// Full 6-row (42-cell) Monday-first grid for the given month, including the
// real leading/trailing dates from adjacent months (dimmed) rather than
// blank filler cells — a week-picker needs the complete row visible even
// when a week spans two months.
const buildCalendarGrid = (year, month) => {
    const first = new Date(year, month, 1);
    const firstDow = (first.getDay() + 6) % 7; // Mon=0 .. Sun=6
    const gridStart = new Date(year, month, 1 - firstDow);
    const cells = [];
    for (let i = 0; i < 42; i++) {
        const d = new Date(gridStart);
        d.setDate(gridStart.getDate() + i);
        cells.push({ date: d, otherMonth: d.getMonth() !== month });
    }
    return cells;
};

const DOW_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

// Prev/next steppers alone don't feel like "a calendar" — this opens an
// actual month grid where hovering any day highlights its whole week (the
// unit this filter actually selects) and clicking picks that week, the same
// interaction users expect from a real calendar week-picker.
function WeekCalendarPicker({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const [popoverPos, setPopoverPos] = useState(null);
    const [viewMonth, setViewMonth] = useState(() => {
        const range = getWeekRange(value);
        const d = range ? new Date(`${range.start}T00:00:00`) : new Date();
        return { year: d.getFullYear(), month: d.getMonth() };
    });
    const [hoverWeek, setHoverWeek] = useState(null);
    const wrapRef = useRef(null);
    const triggerRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        // Re-center the calendar on the selected week's month each time it opens.
        const range = getWeekRange(value);
        const d = range ? new Date(`${range.start}T00:00:00`) : new Date();
        setViewMonth({ year: d.getFullYear(), month: d.getMonth() });
        // The popover is portaled to document.body (see below) so it can
        // escape .hero-actions, which needs overflow-x:auto for its own
        // responsive horizontal-scroll behavior — but per the CSS overflow
        // spec, a non-visible overflow-x forces overflow-y to compute as
        // auto too (an element can't have exactly one axis stay "visible"),
        // which was silently clipping the popover every time it opened.
        // Being outside that ancestor now, position has to be computed
        // manually from the trigger's real screen position.
        const rect = triggerRef.current?.getBoundingClientRect();
        if (rect) setPopoverPos({ top: rect.bottom + 8, left: rect.left });
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const onDocMouseDown = (e) => {
            const insideTrigger = wrapRef.current && wrapRef.current.contains(e.target);
            const insidePopover = e.target.closest && e.target.closest(".wk-cal-popover");
            if (!insideTrigger && !insidePopover) setOpen(false);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, []);

    const cells = React.useMemo(() => buildCalendarGrid(viewMonth.year, viewMonth.month), [viewMonth]);
    const selectedWeek = `${parseWeekValue(value).year}-W${String(parseWeekValue(value).week).padStart(2, "0")}`;

    const shiftMonth = (dir) => {
        setViewMonth((prev) => {
            const d = new Date(prev.year, prev.month + dir, 1);
            return { year: d.getFullYear(), month: d.getMonth() };
        });
    };

    return (
        <div className="wk-cal-wrap" ref={wrapRef}>
            <button type="button" className="hm-week-nav-btn" onClick={() => onChange(stepIsoWeek(value, -1))} aria-label="Previous week">
                <ChevronLeft size={15} />
            </button>
            <button type="button" ref={triggerRef} className="wk-cal-trigger" onClick={() => setOpen((o) => !o)}>
                <Calendar size={13} />
                {formatWeekRangeLabel(value)}
            </button>
            <button type="button" className="hm-week-nav-btn" onClick={() => onChange(stepIsoWeek(value, 1))} aria-label="Next week">
                <ChevronR size={15} />
            </button>

            {open && popoverPos && createPortal(
                <div
                    className="wk-cal-popover"
                    style={{ position: "fixed", top: popoverPos.top, left: popoverPos.left }}
                    onMouseLeave={() => setHoverWeek(null)}
                >
                    <div className="wk-cal-popover-header">
                        <button type="button" className="wk-cal-month-nav" onClick={() => shiftMonth(-1)} aria-label="Previous month">
                            <ChevronLeft size={14} />
                        </button>
                        <span>{MONTH_NAMES[viewMonth.month]} {viewMonth.year}</span>
                        <button type="button" className="wk-cal-month-nav" onClick={() => shiftMonth(1)} aria-label="Next month">
                            <ChevronR size={14} />
                        </button>
                    </div>
                    <div className="wk-cal-dow-row">
                        {DOW_LABELS.map((d) => <span key={d}>{d}</span>)}
                    </div>
                    <div className="wk-cal-grid">
                        {cells.map((cell, i) => {
                            const cellWeek = dateToIsoWeek(cell.date);
                            return (
                                <button
                                    type="button"
                                    key={i}
                                    className={[
                                        "wk-cal-cell",
                                        cell.otherMonth ? "wk-cal-cell--dim" : "",
                                        cellWeek === hoverWeek ? "wk-cal-cell--hover" : "",
                                        cellWeek === selectedWeek ? "wk-cal-cell--selected" : "",
                                    ].filter(Boolean).join(" ")}
                                    onMouseEnter={() => setHoverWeek(cellWeek)}
                                    onClick={() => { onChange(cellWeek); setOpen(false); }}
                                >
                                    {cell.date.getDate()}
                                </button>
                            );
                        })}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

const AnalyticsDashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("monthly");
    const getLocalYmd = (dateObj = new Date()) => {
        return dateObj.getFullYear() + '-' + String(dateObj.getMonth() + 1).padStart(2, '0') + '-' + String(dateObj.getDate()).padStart(2, '0');
    };
    const [dailyDate, setDailyDate] = useState(() => getLocalYmd());
    const [customStart, setCustomStart] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 6);
        return getLocalYmd(d);
    });
    const [customEnd, setCustomEnd] = useState(() => getLocalYmd());
    const [weeklySelection, setWeeklySelection] = useState(() => getCurrentIsoWeek());
    const [monthlySelection, setMonthlySelection] = useState(() => getCurrentMonth());
    const [yearlySelection, setYearlySelection] = useState(() => new Date().getFullYear());


    // Departments State
    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState(null);
    const [showCreateDeptModal, setShowCreateDeptModal] = useState(false);
    const [newDeptName, setNewDeptName] = useState("");
    const [assigningDept, setAssigningDept] = useState(null);

    const [workStart, setWorkStart] = useState("09:00");
    const [workEnd, setWorkEnd] = useState("17:00");
    const [triggerUrls, setTriggerUrls] = useState([]);
    const [triggerInput, setTriggerInput] = useState("");
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [agentModalTab, setAgentModalTab] = useState("overview");
    const [appsPage, setAppsPage] = useState(1);
    const [urlsPage, setUrlsPage] = useState(1);
    const [uploadsPage, setUploadsPage] = useState(1);
    const [changesPage, setChangesPage] = useState(1);
    const [filesExpanded, setFilesExpanded] = useState(false);
    const [modalAppTagFilter, setModalAppTagFilter] = useState("all"); // all | PRODUCTIVE | NON_PRODUCTIVE
    const [modalWebsiteTagFilter, setModalWebsiteTagFilter] = useState("all"); // all | Productive | Unproductive

    // A live data refresh re-sets selectedAgent to a new object for the same
    // person (see the socket-update effect below) — keying off agentId
    // instead of the object reference means pagination only resets when a
    // genuinely different employee is opened, not on every background refresh.
    useEffect(() => {
        setAppsPage(1);
        setUrlsPage(1);
        setUploadsPage(1);
        setChangesPage(1);
        setFilesExpanded(false);
    }, [selectedAgent?.agentId]);
    const [agentCalYear, setAgentCalYear] = useState(() => new Date().getFullYear());
    const [agentCalMonth, setAgentCalMonth] = useState(() => new Date().getMonth());

    // CEO convenience filters
    const [employeeStatusFilter, setEmployeeStatusFilter] = useState("all"); // all | online | offline
    const [workModeFilter, setWorkModeFilter] = useState("all"); // all | overtime | balanced | under
    const [productivityFilter, setProductivityFilter] = useState("all"); // all | high | low
    const [websiteTagFilter, setWebsiteTagFilter] = useState("all"); // all | Productive | Unproductive | Unclassified
    const [appTagFilter, setAppTagFilter] = useState("all"); // all | PRODUCTIVE | NON_PRODUCTIVE | NEUTRAL

    // Team Performance filter — which department to display in the Team Performance table
    const [teamPerfFilter, setTeamPerfFilter] = useState("all"); // "all" or dept._id

    // Guards against out-of-order responses: fetchData/fetchDeptAnalytics both write into
    // `data`, and can overlap when filters change quickly or the 30s poll overlaps a manual
    // refetch. Each call stamps a sequence id; a response only applies if it's still the latest.
    const dataRequestSeqRef = useRef(0);

    const handleExport = async () => {
        const qs = buildAnalyticsQuery();
        if (!qs) return;

        const loadingToast = toast.loading("Generating analytics report...");
        try {
            const payload = Object.fromEntries(new URLSearchParams(qs).entries());
            payload.employeeStatusFilter = employeeStatusFilter;
            payload.workModeFilter = workModeFilter;
            payload.productivityFilter = productivityFilter;
            payload.websiteTagFilter = websiteTagFilter;

            const res = await apiPost("/api/analytics/generate-report", payload);
            const result = await res.json().catch(() => null);
            if (!res.ok || !result?.success) {
                toast.dismiss(loadingToast);
                toast.error(result?.message || "Failed to generate report");
                return;
            }

            const jobId = result?.jobId;
            if (!jobId) {
                toast.dismiss(loadingToast);
                toast.error("Report job id missing");
                return;
            }

            let jobState = null;
            for (let attempt = 0; attempt < 180; attempt += 1) {
                await new Promise((resolve) => setTimeout(resolve, 1500));
                const statusRes = await apiGet(`/api/analytics/report-jobs/${jobId}`);
                const statusJson = await statusRes.json().catch(() => null);
                if (!statusRes.ok || !statusJson?.success) {
                    toast.dismiss(loadingToast);
                    toast.error(statusJson?.message || "Failed to fetch report status");
                    return;
                }

                jobState = statusJson?.job || null;
                if (jobState?.status === "completed" || jobState?.status === "failed") break;
            }

            if (!jobState) {
                toast.dismiss(loadingToast);
                toast.error("Report job status unavailable");
                return;
            }

            if (jobState.status !== "completed") {
                toast.dismiss(loadingToast);
                toast.error(jobState.lastError || "Report generation failed");
                return;
            }

            const fileRes = await apiGet(`/api/analytics/report-jobs/${jobId}/download`);
            if (!fileRes.ok) {
                const errJson = await fileRes.json().catch(() => null);
                toast.dismiss(loadingToast);
                toast.error(errJson?.message || "Report generated but download failed");
                return;
            }

            const blob = await fileRes.blob();
            const contentDisposition = fileRes.headers.get("Content-Disposition") || "";
            const fileNameMatch = /filename\*?=(?:UTF-8''|\"?)([^\";]+)/i.exec(contentDisposition);
            const suggestedFileName = decodeURIComponent((fileNameMatch?.[1] || jobState.fileName || "analytics_report.xlsx").replace(/\"/g, ""));
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = suggestedFileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(objectUrl);

            toast.dismiss(loadingToast);
            toast.success(`Report generated: ${jobState.fileName || suggestedFileName}`);
            if (jobState.relativePath) {
                toast.info(`Saved in backend: ${jobState.relativePath}`);
            }
        } catch (err) {
            console.error("Generate report error:", err);
            toast.dismiss(loadingToast);
            toast.error("Failed to generate report");
        }
    };

    const buildAnalyticsQuery = () => {
        const isValidDate = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
        const d = new Date();
        const today = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        const safeDaily = isValidDate(dailyDate) ? dailyDate : today;
        const safeStart = isValidDate(customStart) ? customStart : today;
        const safeEnd = isValidDate(customEnd) ? customEnd : today;
        const selectedWeekRange = getWeekRange(weeklySelection);
        const selectedMonthRange = getMonthRange(monthlySelection);
        const selectedYearRange = getYearRange(yearlySelection);

        const params = new URLSearchParams();
        if (filter === "daily") {
            params.set("filter", "daily");
            params.set("date", safeDaily);
            return params.toString();
        }

        if (filter === "weekly") {
            if (!selectedWeekRange) {
                toast.error("Weekly filter: choose a valid week");
                return null;
            }
            params.set("filter", "custom");
            params.set("start", selectedWeekRange.start);
            params.set("end", selectedWeekRange.end);
            return params.toString();
        }

        if (filter === "monthly") {
            if (!selectedMonthRange) {
                toast.error("Monthly filter: choose a valid month");
                return null;
            }
            params.set("filter", "custom");
            params.set("start", selectedMonthRange.start);
            params.set("end", selectedMonthRange.end);
            return params.toString();
        }

        if (filter === "yearly") {
            if (!selectedYearRange) {
                toast.error("Yearly filter: choose a valid year");
                return null;
            }
            params.set("filter", "custom");
            params.set("start", selectedYearRange.start);
            params.set("end", selectedYearRange.end);
            return params.toString();
        }

        params.set("filter", filter);
        if (filter === "custom") {
            if (safeStart > safeEnd) {
                toast.error("Custom range: start date must be before end date");
                return null;
            }
            params.set("start", safeStart);
            params.set("end", safeEnd);
        }
        return params.toString();
    };

    useEffect(() => {
        fetchDepartments();
    }, []);

    useEffect(() => {
        // Optimization: Only show skeleton on first load.
        // Subsequent filter changes will refresh data in background.
        const shouldShowSkeleton = !data;
        fetchData(shouldShowSkeleton);

        const interval = setInterval(() => fetchData(false), 30000);

        return () => clearInterval(interval);
    }, [filter, dailyDate, customStart, customEnd, weeklySelection, monthlySelection, yearlySelection, selectedDept]);

    useEffect(() => {
        const handleAttendanceMarked = (payload) => {
            console.log("[SOCKET] Attendance marked event:", payload);
            fetchData(false);
        };

        socket.on("attendance_marked", handleAttendanceMarked);
        return () => socket.off("attendance_marked", handleAttendanceMarked);
    }, []);

    const fetchData = async (showSkeleton = false) => {
        const requestId = ++dataRequestSeqRef.current;
        if (showSkeleton) setLoading(true);
        try {
            const qs = buildAnalyticsQuery();
            if (!qs) return;
            const url = selectedDept?._id
                ? `/api/departments/${selectedDept._id}/analytics?${qs}`
                : `/api/analytics/ceo-dashboard?${qs}`;

            const res = await apiGet(url);
            const result = await res.json().catch(() => null);
            if (requestId !== dataRequestSeqRef.current) return; // superseded by a newer request
            if (!res.ok) {
                toast.error(result?.message || "Failed to load analytics");
                return;
            }
            if (result?.success) {
                setData(result);
                setWorkStart(result.summary?.workStart || result.settings?.workStart || "09:00");
                setWorkEnd(result.summary?.workEnd || result.settings?.workEnd || "17:00");
                setTriggerUrls(result.summary?.attendanceTriggerUrls || []);
            }
        } catch (err) {
            console.error("Dashboard fetch error:", err);
            if (requestId === dataRequestSeqRef.current) toast.error("Failed to load analytics");
        } finally {
            if (requestId === dataRequestSeqRef.current) setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const res = await apiGet("/api/departments");
            const result = await res.json();
            if (result.success) setDepartments(result.departments);
        } catch (err) {
            toast.error("Failed to load departments");
        }
    };

    const fetchDeptAnalytics = async (deptId, showSkeleton = true) => {
        const requestId = ++dataRequestSeqRef.current;
        if (showSkeleton) setLoading(true);
        try {
            const qs = buildAnalyticsQuery();
            if (!qs) return;
            const res = await apiGet(`/api/departments/${deptId}/analytics?${qs}`);
            const result = await res.json().catch(() => null);
            if (requestId !== dataRequestSeqRef.current) return; // superseded by a newer request
            if (!res.ok) {
                toast.error(result?.message || "Failed to load team analytics");
                return;
            }
            if (result?.success) {
                setData(result);
                setSelectedDept(result.department);
            }
        } catch (err) {
            if (requestId === dataRequestSeqRef.current) toast.error("Failed to load team analytics");
        } finally {
            if (requestId === dataRequestSeqRef.current) setLoading(false);
        }
    };

    const handleCreateDept = async () => {
        if (!newDeptName) return toast.error("Name required");
        try {
            const res = await apiPost("/api/departments", { name: newDeptName });
            if (res.ok) {
                toast.success("Team created");
                setShowCreateDeptModal(false);
                setNewDeptName("");
                fetchDepartments();
            }
        } catch (err) {
            toast.error("Failed to create team");
        }
    };

    const handleAssignAgents = async (deptId, agentIds) => {
        try {
            const res = await apiPut("/api/departments/agents", { departmentId: deptId, agentIds });
            if (res.ok) {
                toast.success("Agents assigned");
                setAssigningDept(null);
                fetchDepartments();
            }
        } catch (err) {
            toast.error("Assignment failed");
        }
    };

    const handleDeleteDept = async (id) => {
        if (!window.confirm("Delete this team?")) return;
        try {
            const res = await apiDelete(`/api/departments/${id}`);
            if (res.ok) {
                toast.success("Deleted");
                fetchDepartments();
                if (selectedDept?._id === id) setSelectedDept(null);
            }
        } catch (err) {
            toast.error("Delete failed");
        }
    };



    const formatTime = (ms) => {
        if (!ms || ms < 0) return "0h 0m";
        const hours = Math.floor(ms / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
    };
    const getWebsiteTagLabel = (tag) => {
        if (tag === "Unproductive") return "Non-Productive";
        return "Productive";
    };

    const parseHourValue = (timeVal) => {
        if (!timeVal) return 0;
        const [h = "0", m = "0"] = String(timeVal).split(":");
        const hours = Number(h);
        const mins = Number(m);
        if (!Number.isFinite(hours) || !Number.isFinite(mins)) return 0;
        return hours + (mins / 60);
    };

    const { summary, employees, topApps, topWebsites, trend, categorySplit, teamProductivity } = data || {};
    
    // ⭐ Real-time Sync: Update selectedAgent when employees data changes
    useEffect(() => {
        if (selectedAgent && employees) {
            const updated = employees.find(e => e.agentId === selectedAgent.agentId);
            if (updated && updated !== selectedAgent) {
                setSelectedAgent(updated);
            }
        }
    }, [employees, selectedAgent]);

    const fallbackDaysInPeriod = filter === "daily" ? 1 : filter === "weekly" ? 5 : 22;
    const summaryShiftStart = summary?.workStart || workStart;
    const summaryShiftEnd = summary?.workEnd || workEnd;
    const summaryShiftHoursPerDay = Number(summary?.workHoursPerDay);
    const shiftHoursPerDay = Number.isFinite(summaryShiftHoursPerDay) && summaryShiftHoursPerDay > 0
        ? summaryShiftHoursPerDay
        : Math.max(0, parseHourValue(summaryShiftEnd) - parseHourValue(summaryShiftStart));
    const workDaysInPeriod = summary?.workDaysInPeriod != null ? Number(summary.workDaysInPeriod) : fallbackDaysInPeriod;
    const configuredHoursPerDay = shiftHoursPerDay > 0 ? shiftHoursPerDay : 8;
    const configuredPerEmployeeMs = configuredHoursPerDay * workDaysInPeriod * 3600 * 1000;
    const totalEmployees = Number(summary?.totalEmployees || employees?.length || 0);
    const getEmployeeWorkedMs = (emp) => {
        const workedMs = Number(emp?.workedMs || 0);
        if (workedMs > 0) return workedMs;
        const tracked = Number(emp?.trackedActiveMs || 0) + Number(emp?.trackedIdleMs || 0);
        if (tracked > 0) return tracked;
        return Number(emp?.totalOnlineMs || 0);
    };
    const orgActualWorkMs = Number(summary?.totalUsageTimeMs || (employees || []).reduce((sum, emp) => sum + getEmployeeWorkedMs(emp), 0) || 0);
    const orgConfiguredCapacityMs = configuredPerEmployeeMs * totalEmployees;
    const orgConfiguredUtilizationPct = orgConfiguredCapacityMs > 0 ? ((orgActualWorkMs / orgConfiguredCapacityMs) * 100) : 0;
    const capacityGapMs = orgActualWorkMs - orgConfiguredCapacityMs;
    const avgUtilizationPct = Number.isFinite(Number(summary?.avgUtilization))
        ? Number(summary?.avgUtilization)
        : orgConfiguredUtilizationPct;
    const capacityHealth = avgUtilizationPct > 100
        ? { label: "Overloaded", color: "#ff5630", bg: "#fff0ec" }
        : avgUtilizationPct >= 80
            ? { label: "Healthy", color: "#36b37e", bg: "#e3fcef" }
            : { label: "Under-loaded", color: "#ffab00", bg: "#fffae5" };

    // Work status: compare actual hours vs configured shift baseline.
    const getWorkStatus = (emp) => {
        const expectedMs = configuredPerEmployeeMs;
        const actual = getEmployeeWorkedMs(emp);
        const ratio = expectedMs > 0 ? actual / expectedMs : 0;
        if (ratio > 1) return { label: 'Overtime', color: '#ff5630', bg: '#fff0ec' };
        return { label: 'On Track', color: '#36b37e', bg: '#e3fcef' };
    };

    const overtimeCount = employees?.filter(e => getWorkStatus(e).label === 'Overtime').length || 0;
    const balancedCount = employees?.filter(e => getWorkStatus(e).label === 'On Track').length || 0;
    const overtimeMsTotal = (employees || []).reduce((sum, emp) => {
        const actual = getEmployeeWorkedMs(emp);
        return sum + Math.max(0, actual - configuredPerEmployeeMs);
    }, 0);

    const visibleEmployees = React.useMemo(() => {
        return (employees || []).filter((e) => {
            if (employeeStatusFilter === "online" && !e.isActive) return false;
            if (employeeStatusFilter === "offline" && e.isActive) return false;

            const wm = getWorkStatus(e).label;
            if (workModeFilter === "overtime" && wm !== "Overtime") return false;
            if (workModeFilter === "balanced" && wm !== "On Track") return false;

            const pct = Number(e.productivityPct ?? 0);
            if (productivityFilter === "high" && pct < 75) return false;
            if (productivityFilter === "low" && pct >= 75) return false;
            return true;
        });
    }, [employees, employeeStatusFilter, workModeFilter, productivityFilter, configuredPerEmployeeMs]);

    const visibleWebsites = React.useMemo(() => {
        return (topWebsites || []).filter((w) => {
            if (websiteTagFilter === "all") return true;
            return (w.productivityTag || "Productive") === websiteTagFilter;
        });
    }, [topWebsites, websiteTagFilter]);

    const visibleApps = React.useMemo(() => {
        return (topApps || []).filter((app) => {
            if (appTagFilter === "all") return true;
            return (app.productivityType || "PRODUCTIVE") === appTagFilter;
        });
    }, [topApps, appTagFilter]);

    const hasAnyUsage = !!(
        (summary?.totalTrackedActiveMs > 0) ||
        (summary?.totalUsageTimeMs > 0) ||
        (topApps && topApps.length > 0) ||
        (topWebsites && topWebsites.length > 0)
    );

    const noDataMessage = (() => {
        if (hasAnyUsage) return null;
        const avail = summary?.availableFrom;
        if (avail) return `No data for the selected period. Data is only available after ${avail}.`;
        return "No data for the selected period.";
    })();

    return (
        <div className="analytics-container" style={{ position: "relative" }}>
            {/* Main Content Area */}
            {loading && !data ? (
                <main className="zoho-main">
                    <div className="zoho-body">
                        <div className="zoho-grid">
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="zoho-card col-2">
                                    <div className="card-body kpi-wrapper">
                                        <Skeleton type="text" width="60%" />
                                        <Skeleton type="title" height="26px" style={{ margin: '8px 0' }} />
                                        <Skeleton type="text" width="80%" />
                                    </div>
                                </div>
                            ))}

                            <div className="zoho-card col-8">
                                <div className="card-header"><Skeleton type="text" width="200px" /></div>
                                <div className="card-body" style={{ height: '300px' }}><Skeleton type="card" height="100%" /></div>
                            </div>
                            <div className="zoho-card col-4">
                                <div className="card-header"><Skeleton type="text" width="150px" /></div>
                                <div className="card-body" style={{ height: '300px' }}><Skeleton type="card" height="100%" /></div>
                            </div>

                            <div className="zoho-card col-12">
                                <div className="card-header"><Skeleton type="text" width="180px" /></div>
                                <div className="card-body" style={{ height: '400px' }}><Skeleton type="card" height="100%" /></div>
                            </div>
                        </div>
                    </div>
                </main>
            ) : (
                <main className="zoho-main">
                    <div className="zoho-body">
                        <div className="dashboard-hero">
                            <div className="hero-actions">
                                <PillSelect
                                    value={selectedDept?._id || "org"}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "org") {
                                            setSelectedDept(null);
                                            return;
                                        }
                                        const dept = (departments || []).find(d => d._id === val);
                                        if (dept) setSelectedDept(dept);
                                    }}
                                >
                                    <option value="org">Teams</option>
                                    {(departments || []).map(d => (
                                        <option key={d._id} value={d._id}>Team: {d.name}</option>
                                    ))}
                                </PillSelect>

                                <PillSelect
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                >
                                    <option value="daily">Day</option>
                                    <option value="weekly">Week</option>
                                    <option value="monthly">Month</option>
                                    <option value="yearly">Year</option>
                                    <option value="custom">Custom</option>
                                </PillSelect>

                                {filter === "daily" && (
                                    <input
                                        type="date"
                                        className="gc-pill"
                                        value={dailyDate}
                                        onChange={(e) => setDailyDate(e.target.value)}
                                    />
                                )}

                                {filter === "weekly" && (
                                    <WeekCalendarPicker value={weeklySelection} onChange={setWeeklySelection} />
                                )}

                                {filter === "monthly" && (
                                    <>
                                        <PillSelect
                                            value={parseMonthValue(monthlySelection).year}
                                            onChange={(e) => setMonthlySelection(`${e.target.value}-${String(parseMonthValue(monthlySelection).month).padStart(2, "0")}`)}
                                        >
                                            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                                        </PillSelect>
                                        <PillSelect
                                            value={parseMonthValue(monthlySelection).month}
                                            onChange={(e) => setMonthlySelection(`${parseMonthValue(monthlySelection).year}-${String(e.target.value).padStart(2, "0")}`)}
                                        >
                                            {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                                        </PillSelect>
                                    </>
                                )}

                                {filter === "yearly" && (
                                    <PillSelect
                                        value={yearlySelection}
                                        onChange={(e) => setYearlySelection(e.target.value)}
                                    >
                                        {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                                    </PillSelect>
                                )}

                                {filter === "custom" && (
                                    <div className="gc-pill" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <input
                                            type="date"
                                            value={customStart}
                                            onChange={(e) => setCustomStart(e.target.value)}
                                            style={{ border: "none", background: "transparent", color: "inherit", font: "inherit", padding: 0 }}
                                        />
                                        <span style={{ opacity: 0.6 }}>→</span>
                                        <input
                                            type="date"
                                            value={customEnd}
                                            onChange={(e) => setCustomEnd(e.target.value)}
                                            style={{ border: "none", background: "transparent", color: "inherit", font: "inherit", padding: 0 }}
                                        />
                                    </div>
                                )}
                                <button
                                    className="gc-btn gc-btn-ghost"
                                    onClick={handleExport}
                                    title="Generate Backend Excel Report"
                                >
                                    <Download size={14} />
                                    Generate Report
                                </button>
                            </div>
                        </div>

                        {data && data.hasConfiguredRules === false ? (
                            <div style={{
                                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                padding: "60px 20px", marginTop: "20px", border: "1px dashed rgba(255,255,255,0.2)", borderRadius: "8px",
                                backgroundColor: "rgba(255,255,255,0.02)", color: "var(--zoho-text-primary)"
                            }}>
                                <Settings size={48} color="var(--zoho-text-muted)" style={{ marginBottom: "16px" }} />
                                <h2 style={{ fontSize: "20px", fontWeight: "700", marginBottom: "8px" }}>Dashboard Rules Not Configured</h2>
                                <p style={{ color: "var(--zoho-text-secondary)", marginBottom: "24px", maxWidth: "450px", textAlign: "center" }}>
                                    To view your organization's analytics, you must first configure App and URL productivity rules.
                                    This ensures the data reflects your company's actual productivity standards.
                                </p>
                                <button className="btn-zoho primary" onClick={() => navigate("/settings")} style={{ padding: "10px 20px", fontSize: "14px", fontWeight: "600", borderRadius: "6px" }}>
                                    Configure Rules in Settings
                                </button>
                            </div>
                        ) : (
                            <div className="zoho-grid">
                                {noDataMessage && (
                                    <div className="zoho-card col-12">
                                        <div className="card-body" style={{ padding: 14, color: 'var(--zoho-text-secondary)', display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <Info size={16} color="var(--zoho-text-muted)" />
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>{noDataMessage}</span>
                                        </div>
                                    </div>
                                )}
                                {/* Row 1: KPI Cards */}
                                <div className="zoho-card col-3">
                                    <div className="card-body kpi-wrapper">
                                        <div className="kpi-top-row">
                                            <span className="kpi-subtext">PRODUCTIVITY</span>
                                            <span className="kpi-icon-badge accent"><TrendingUp size={15} /></span>
                                        </div>
                                        <div className="kpi-value" style={{ fontSize: 32, color: (summary?.productivityScore ?? 0) >= 70 ? '#36b37e' : (summary?.productivityScore ?? 0) >= 40 ? '#ffab00' : '#ff5630' }}>
                                            {summary?.productivityScore ?? 0}<span style={{ fontSize: 16, color: 'var(--zoho-text-secondary)' }}>/100</span>
                                        </div>
                                        <div className="kpi-subtext">Team score</div>
                                    </div>
                                </div>
                                <div className="zoho-card col-3">
                                    <div className="card-body kpi-wrapper">
                                        <div className="kpi-top-row">
                                            <span className="kpi-subtext">ONLINE NOW</span>
                                            <span className="kpi-icon-badge success"><Users size={15} /></span>
                                        </div>
                                        <div className="kpi-value" style={{ fontSize: 32 }}>{summary?.activeEmployees || 0} <span style={{ fontSize: 16, color: 'var(--zoho-text-secondary)' }}>/ {summary?.totalEmployees || 0}</span></div>
                                        <div className="kpi-subtext">Employees active</div>
                                    </div>
                                </div>
                                <div className="zoho-card col-3">
                                    <div className="card-body kpi-wrapper">
                                        <div className="kpi-top-row">
                                            <span className="kpi-subtext">WORK DONE</span>
                                            <span className="kpi-icon-badge info"><Clock size={15} /></span>
                                        </div>
                                        <div className="kpi-value" style={{ fontSize: 32 }}>{formatTime(summary?.productiveTimeMs)}</div>
                                        <div className="kpi-subtext">{Number(summary?.productivityPct ?? 0)}% of {formatTime(summary?.totalUsageTimeMs)} tracked</div>
                                    </div>
                                </div>
                                <div className="zoho-card col-3">
                                    <div className="card-body kpi-wrapper">
                                        <div className="kpi-top-row">
                                            <span className="kpi-subtext">RESOURCE UTILIZATION</span>
                                            <span className="kpi-icon-badge warning"><Activity size={15} /></span>
                                        </div>
                                        <div className="kpi-value" style={{ fontSize: 32 }}>{avgUtilizationPct.toFixed(1)}%</div>
                                        <div className="kpi-subtext">{formatTime(orgActualWorkMs)} of {formatTime(orgConfiguredCapacityMs)} shift</div>
                                    </div>
                                </div>

                                {/* Row 2: Charts */}
                                <div className="zoho-card col-8">
                                    <div className="card-header">
                                        <h3>Workforce Output Trend</h3>
                                    </div>
                                    <div className="card-body" style={{ height: 320 }}>
                                        <TrendAreaChart data={trend || []} />
                                    </div>
                                </div>
                                <div className="zoho-card col-4">
                                    <div className="card-header">
                                        <h3>Time Allocation</h3>
                                    </div>
                                    <div className="card-body" style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <DeepWorkDonut summary={summary} />
                                    </div>
                                </div>

                                {/* Row 3: Company Apps & Websites */}
                                <div className="zoho-card col-6">
                                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Layout size={14} style={{ opacity: 0.7 }} /> {selectedDept ? `${selectedDept.name} — Apps Used` : 'Company Apps Used'}
                                        </h3>
                                        {visibleApps && visibleApps.length > 0 && (
                                            <span style={{ fontSize: 11, color: 'var(--zoho-text-muted)', fontWeight: 600 }}>{visibleApps.length} apps</span>
                                        )}
                                    </div>
                                    <div className="card-body" style={{ padding: 0, height: 360, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                        <div className="websites-filter-bar">
                                            {[{ key: 'all', label: 'All' }, { key: 'PRODUCTIVE', label: 'Productive' }, { key: 'NON_PRODUCTIVE', label: 'Non-Productive' }].map(({ key, label }) => (
                                                <button key={key} onClick={() => setAppTagFilter(key)}
                                                    className={`websites-filter-btn${appTagFilter === key ? ' active' : ''}`}>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="analytics-scrollable-list" style={{ flex: 1, overflowY: 'auto' }}>
                                            {visibleApps && visibleApps.length > 0 ? (
                                                (() => {
                                                    const totalMs = visibleApps.reduce((s, a) => s + (a.totalTimeMs || a.durationMs || 0), 0);
                                                    return (
                                                        <div className="analytics-list-container">
                                                            {visibleApps.slice(0, 15).map((app, i) => {
                                                                const appName = app.name || app.appName || '—';
                                                                const appDuration = app.totalTimeMs || app.durationMs || 0;
                                                                const pType = app.productivityType === 'NON_PRODUCTIVE' ? 'NON_PRODUCTIVE' : 'PRODUCTIVE';
                                                                const pTypeColor = pType === 'NON_PRODUCTIVE' ? '#ff5630' : '#36b37e';
                                                                const pTypeBg = pType === 'NON_PRODUCTIVE' ? 'rgba(255,86,48,0.12)' : 'rgba(54,179,126,0.12)';
                                                                const pTypeLabel = pType === 'NON_PRODUCTIVE' ? 'Non-Productive' : 'Productive';
                                                                const pct = totalMs > 0 ? (appDuration / totalMs) * 100 : 0;
                                                                return (
                                                                    <div key={i} className="analytics-list-row">
                                                                        <span className="analytics-list-rank">{i + 1}</span>
                                                                        <div className="analytics-list-icon" style={{ background: pTypeBg, border: `1px solid ${pTypeColor}30`, color: pTypeColor }}>
                                                                            {(appName || '?').charAt(0).toUpperCase()}
                                                                        </div>
                                                                        <div className="analytics-list-info">
                                                                            <div className="analytics-list-name">{appName}</div>
                                                                            <div className="analytics-list-progress">
                                                                                <div style={{ height: '100%', width: `${pct.toFixed(1)}%`, background: pTypeColor, borderRadius: 2, transition: 'width 0.4s' }} />
                                                                            </div>
                                                                        </div>
                                                                        <span className="analytics-list-time">{formatTime(appDuration)}</span>
                                                                        <span className="analytics-list-meta">{app.adoptionRate ?? 0}% adoption</span>
                                                                        <span className="analytics-list-badge" style={{ background: pTypeBg, color: pTypeColor, border: `1px solid ${pTypeColor}40` }}>{pTypeLabel}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', color: 'var(--zoho-text-secondary)' }}>
                                                    <Layout size={28} color="var(--zoho-text-faint)" style={{ marginBottom: 10 }} />
                                                    <div style={{ fontSize: 13 }}>No app usage data for this period</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="zoho-card col-6">
                                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Globe size={14} style={{ opacity: 0.7 }} /> {selectedDept ? `${selectedDept.name} — Websites Visited` : 'Company Websites Visited'}
                                        </h3>
                                        {visibleWebsites && visibleWebsites.length > 0 && (
                                            <span style={{ fontSize: 11, color: 'var(--zoho-text-muted)', fontWeight: 600 }}>{visibleWebsites.length} sites</span>
                                        )}
                                    </div>
                                    <div className="card-body" style={{ padding: 0, height: 360, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                        <div className="websites-filter-bar">
                                            {[{ key: 'all', label: 'All' }, { key: 'Productive', label: 'Productive' }, { key: 'Unproductive', label: 'Non-Productive' }].map(({ key, label }) => (
                                                <button key={key} onClick={() => setWebsiteTagFilter(key)}
                                                    className={`websites-filter-btn${websiteTagFilter === key ? ' active' : ''}`}>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="analytics-scrollable-list" style={{ flex: 1, overflowY: 'auto' }}>
                                            {visibleWebsites && visibleWebsites.length > 0 ? (
                                                (() => {
                                                    const totalMs = (visibleWebsites || []).reduce((s, w) => s + (w.durationMs || 0), 0);
                                                    return (
                                                        <div className="analytics-list-container">
                                                            {visibleWebsites.slice(0, 15).map((site, i) => {
                                                                const isWork = site.productivityTag === 'Productive';
                                                                const isNonWork = site.productivityTag === 'Unproductive';
                                                                const tagColor = isWork ? '#36b37e' : isNonWork ? '#ff5630' : 'var(--zoho-text-muted)';
                                                                const tagBg = isWork ? 'rgba(54,179,126,0.12)' : isNonWork ? 'rgba(255,86,48,0.12)' : 'rgba(255,255,255,0.05)';
                                                                const pct = totalMs > 0 ? (site.durationMs / totalMs) * 100 : 0;
                                                                return (
                                                                    <div key={i} className="analytics-list-row">
                                                                        <span className="analytics-list-rank">{i + 1}</span>
                                                                        <div className="analytics-list-icon" style={{ background: tagBg, border: `1px solid ${tagColor}30` }}>
                                                                            <Globe size={13} color={tagColor} />
                                                                        </div>
                                                                        <div className="analytics-list-info">
                                                                            <div className="analytics-list-name">{site.domain || '—'}</div>
                                                                            <div className="analytics-list-progress">
                                                                                <div style={{ height: '100%', width: `${pct.toFixed(1)}%`, background: tagColor, borderRadius: 2, transition: 'width 0.4s' }} />
                                                                            </div>
                                                                        </div>
                                                                        <span className="analytics-list-time">{formatTime(site.durationMs)}</span>
                                                                        <span className="analytics-list-meta">{site.visits ?? 0} visits</span>
                                                                        <span className="analytics-list-badge" style={{ background: tagBg, color: tagColor, border: `1px solid ${tagColor}40` }}>{getWebsiteTagLabel(site.productivityTag)}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', color: 'var(--zoho-text-secondary)' }}>
                                                    <Globe size={28} color="var(--zoho-text-faint)" style={{ marginBottom: 10 }} />
                                                    <div style={{ fontSize: 13 }}>No website data for this period</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Row 4: Team Performance Table with Team Filter */}
                                <div className="zoho-card col-12">
                                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Users size={14} /> Team Performance & Focus
                                            {teamPerfFilter !== 'all' && teamPerfFilter !== 'unassigned' && (
                                                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: 'rgba(0,82,204,0.12)', color: '#0052cc', marginLeft: 4 }}>
                                                    {departments.find(d => d._id === teamPerfFilter)?.name || ''}
                                                </span>
                                            )}
                                        </h3>
                                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--zoho-text-secondary)' }}>
                                                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#ff5630', flexShrink: 0 }} /> Overtime
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--zoho-text-secondary)' }}>
                                                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#36b37e', flexShrink: 0 }} /> On Track
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, borderLeft: '1px solid var(--zoho-border)', paddingLeft: 12 }}>
                                                <select
                                                    value={teamPerfFilter}
                                                    onChange={e => setTeamPerfFilter(e.target.value)}
                                                    style={{ border: '1px solid var(--zoho-border)', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, background: 'var(--zoho-surface, rgba(255,255,255,0.06))', color: 'var(--zoho-text-primary)', cursor: 'pointer', minWidth: 140, outline: 'none' }}
                                                >
                                                    <option value="all">All Teams</option>
                                                    {(departments || []).map(d => (
                                                        <option key={d._id} value={d._id}>{d.name}</option>
                                                    ))}
                                                    <option value="unassigned">Unassigned</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body" style={{ padding: 0 }}>
                                        <div className="zoho-table-wrap">
                                            <table className="zoho-table">
                                                <thead>
                                                    <tr>
                                                        <th>Team Member</th>
                                                        <th>Team</th>
                                                        <th>Status</th>
                                                        <th>Productivity Score</th>
                                                        <th>Time Worked</th>
                                                        <th>Work Balance</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        const teamFiltered = visibleEmployees?.filter(emp => {
                                                            if (teamPerfFilter === 'all') return true;
                                                            if (teamPerfFilter === 'unassigned') return !emp.departmentName || emp.departmentName === 'Unassigned';
                                                            const dept = departments.find(d => d._id === teamPerfFilter);
                                                            return dept && emp.departmentName === dept.name;
                                                        }) || [];
                                                        if (teamFiltered.length === 0) return (
                                                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: 'var(--zoho-text-secondary)' }}>No employees found for this team filter.</td></tr>
                                                        );
                                                        return teamFiltered.slice(0, 50).map((emp, i) => {
                                                            const ws = getWorkStatus(emp);
                                                            const pScore = Number(emp.productivityScore ?? 0);
                                                            return (
                                                                <tr key={i} onClick={() => setSelectedAgent(emp)} style={{ cursor: 'pointer' }}>
                                                                    <td>
                                                                        <div className="user-badge">
                                                                            <div className="user-avatar-mini" style={{ background: emp.isActive ? '#36b37e' : '#64748b' }}>
                                                                                {displayName(emp).charAt(0)}
                                                                            </div>
                                                                            <div>
                                                                                <div style={{ fontWeight: 600, color: 'var(--zoho-text-primary)' }}>{displayName(emp)}</div>
                                                                                <div style={{ fontSize: 11, color: 'var(--zoho-text-secondary)' }}>{emp.details?.hostname || '—'}</div>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: emp.departmentName && emp.departmentName !== 'Unassigned' ? 'rgba(0,82,204,0.1)' : 'rgba(255,255,255,0.06)', color: emp.departmentName && emp.departmentName !== 'Unassigned' ? '#0052cc' : 'var(--zoho-text-muted)' }}>
                                                                            {emp.departmentName || 'Unassigned'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: emp.isActive ? '#36b37e' : 'var(--zoho-text-faint)' }}>
                                                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: emp.isActive ? '#36b37e' : 'var(--zoho-text-faint)' }}></div>
                                                                            {emp.isActive ? 'Online' : 'Offline'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                            <span style={{ fontWeight: 700, color: pScore >= 70 ? '#36b37e' : pScore >= 40 ? '#ffab00' : '#ff5630', width: 24 }}>{pScore}</span>
                                                                            <div className="progress-tube" style={{ width: 80 }}><div className="progress-tube-fill" style={{ width: `${pScore}%`, background: pScore >= 70 ? '#36b37e' : pScore >= 40 ? '#ffab00' : '#ff5630' }}></div></div>
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ fontWeight: 600, color: 'var(--zoho-text-primary)' }}>{formatTime(getEmployeeWorkedMs(emp))}</td>
                                                                    <td>
                                                                        <span style={{ padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, color: ws.color, background: ws.bg }}>
                                                                            {ws.label}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        });
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                    )}
                </div>
            </main>
            )}

            {/* Create Dept Modal */}
            {showCreateDeptModal && (
                <div className="modal-overlay" onClick={() => setShowCreateDeptModal(false)}>
                    <div className="zoho-modal" onClick={e => e.stopPropagation()}>
                        <div className="card-header">
                            <h3>Create New Team</h3>
                            <button className="btn-icon" onClick={() => setShowCreateDeptModal(false)}><X size={20} /></button>
                        </div>
                        <div className="card-body">
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Team Name</label>
                            <input type="text" value={newDeptName} onChange={e => setNewDeptName(e.target.value)} placeholder="e.g. Engineering, Marketing..." style={{ width: '100%', padding: '0.8rem', border: '1px solid var(--zoho-border)', background: 'rgba(255,255,255,0.06)', color: 'var(--zoho-text-primary)', borderRadius: 4, marginBottom: 20 }} />
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                <button className="btn-zoho" onClick={() => setShowCreateDeptModal(false)}>Cancel</button>
                                <button className="btn-zoho btn-zoho-primary" onClick={handleCreateDept}>Create Team</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Agents Modal */}
            {assigningDept && (
                <div className="modal-overlay" onClick={() => setAssigningDept(null)}>
                    <div className="zoho-modal" style={{ maxWidth: 800 }} onClick={e => e.stopPropagation()}>
                        <div className="card-header">
                            <h3>Assign Team Members to Team: {assigningDept.name}</h3>
                            <button className="btn-icon" onClick={() => setAssigningDept(null)}><X size={20} /></button>
                        </div>
                        <div className="card-body">
                            <AgentMultiSelect
                                allAgents={employees || []}
                                selectedAgentIds={assigningDept.agents || []}
                                onSave={(ids) => handleAssignAgents(assigningDept._id, ids)}
                                onCancel={() => setAssigningDept(null)}
                            />
                        </div>
                    </div>
                </div>
            )}



            {/* Employee Detail Modal — Full Side Panel with Tabs */}

            {/* Employee Detail Modal — Full Side Panel with Tabs */}
            {selectedAgent && (() => {
                const allEmps = employees || [];
                const currentIdx = allEmps.findIndex(e => e.agentId === selectedAgent.agentId);
                const goPrev = () => { if (currentIdx > 0) { setSelectedAgent(allEmps[currentIdx - 1]); setAgentModalTab('overview'); } };
                const goNext = () => { if (currentIdx < allEmps.length - 1) { setSelectedAgent(allEmps[currentIdx + 1]); setAgentModalTab('overview'); } };
                const ws = getWorkStatus(selectedAgent);

                // Build 30-day attendance grid from dailyOnlineTime
                const today = new Date();
                const attendanceDays = Array.from({ length: 30 }, (_, i) => {
                    const d = new Date(today); d.setDate(d.getDate() - (29 - i));
                    const dStr = d.toISOString().split('T')[0];
                    const dayData = (selectedAgent.details?.dailyOnlineTime || []).find(x => x.date === dStr);
                    const attLog = (selectedAgent.attendance || []).find(x => x.date === dStr);

                    const ms = dayData?.onlineMs || 0;
                    const workHrs = configuredHoursPerDay * 3600 * 1000;
                    const pct = workHrs > 0 ? (ms / workHrs) : 0;

                    return {
                        date: dStr,
                        ms,
                        pct,
                        day: d.toLocaleDateString('en', { weekday: 'short' }),
                        label: d.getDate(),
                        checkIn: attLog?.checkInTime ? new Date(attLog.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—',
                        triggeredBy: attLog?.triggeredBy || '—'
                    };
                });

                const navItems = [
                    { id: 'overview', icon: <User size={16} />, label: 'Overview' },
                    { id: 'attendance', icon: <Clock size={16} />, label: 'Daily Work' },
                    { id: 'apps', icon: <BarChart2 size={16} />, label: 'Used Apps' },
                    { id: 'web', icon: <Globe size={16} />, label: 'Visited Websites' },
                    {
                        id: 'files', icon: <FileText size={16} />, label: 'File Activity',
                        children: [
                            { id: 'uploads', icon: <FileText size={14} />, label: 'File Upload' },
                            { id: 'changes', icon: <Edit3 size={14} />, label: 'File Changes' },
                        ],
                    },
                ];
                const filesGroup = navItems.find(n => n.id === 'files');
                const isFilesTabActive = filesGroup.children.some(c => c.id === agentModalTab);

                return (
                    <div className="modal-overlay" onClick={() => setSelectedAgent(null)}>
                        <div className="emp-drawer" onClick={e => e.stopPropagation()}>

                            {/* Left Nav */}
                            <div className="emp-drawer-sidebar">
                                {/* Avatar */}
                                <div style={{ padding: '28px 20px 20px', borderBottom: '1px solid var(--zoho-border)' }}>
                                    <div style={{ width: 52, height: 52, borderRadius: 12, background: '#0052cc', color: 'white', fontSize: 20, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                                        {displayName(selectedAgent).charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--zoho-text-primary)' }}>{displayName(selectedAgent)}</div>
                                    <div style={{ fontSize: 11, color: 'var(--zoho-text-secondary)', marginTop: 2 }}>{selectedAgent.details?.hostname}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8 }}>
                                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: selectedAgent.isActive ? '#36b37e' : 'var(--zoho-text-faint)' }}></div>
                                        <span style={{ fontSize: 11, color: 'var(--zoho-text-secondary)' }}>{selectedAgent.isActive ? 'Online' : 'Offline'}</span>
                                        <span style={{ marginLeft: 6, padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: ws.color, background: ws.bg }}>{ws.label}</span>
                                    </div>
                                </div>
                                {/* Nav items */}
                                <nav style={{ padding: '12px 0' }}>
                                    {navItems.map(n => n.children ? (
                                        <div key={n.id}>
                                            <div
                                                className={`emp-nav-item${isFilesTabActive ? ' group-active' : ''}`}
                                                onClick={() => setFilesExpanded(exp => !exp)}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                            >
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{n.icon} <span>{n.label}</span></span>
                                                {filesExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                            </div>
                                            {filesExpanded && n.children.map(c => (
                                                <div
                                                    key={c.id}
                                                    className={`emp-nav-item${agentModalTab === c.id ? ' active' : ''}`}
                                                    onClick={() => setAgentModalTab(c.id)}
                                                    style={{ paddingLeft: 44 }}
                                                >
                                                    {c.icon} <span>{c.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div
                                            key={n.id}
                                            className={`emp-nav-item${agentModalTab === n.id ? ' active' : ''}`}
                                            onClick={() => { setAgentModalTab(n.id); setFilesExpanded(false); }}
                                        >
                                            {n.icon} <span>{n.label}</span>
                                        </div>
                                    ))}
                                </nav>
                                {/* Prev/Next navigation — same icon + "n / total" pill pattern as the Devices page pagination */}
                                <div style={{ marginTop: 'auto', padding: '16px 20px', borderTop: '1px solid var(--zoho-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                    <button className="gc-icon-btn" onClick={goPrev} disabled={currentIdx === 0} data-tip="Previous" aria-label="Previous" style={{ width: 28, height: 28 }}>
                                        <ChevronLeft size={15} />
                                    </button>
                                    <span style={{ minWidth: 44, padding: '4px 10px', borderRadius: 999, background: 'var(--bg-hover)', border: '1px solid var(--zoho-border)', fontSize: '0.76rem', textAlign: 'center', color: 'var(--zoho-text-secondary)' }}>
                                        {currentIdx + 1} / {allEmps.length}
                                    </span>
                                    <button className="gc-icon-btn" onClick={goNext} disabled={currentIdx === allEmps.length - 1} data-tip="Next" aria-label="Next" style={{ width: 28, height: 28 }}>
                                        <ChevronRight size={15} />
                                    </button>
                                </div>
                            </div>

                            {/* Right Content */}
                            <div className="emp-drawer-content">
                                {/* Header */}
                                <div className="emp-drawer-header">
                                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                                        {navItems.find(n => n.id === agentModalTab)?.label
                                            || filesGroup.children.find(c => c.id === agentModalTab)?.label}
                                    </h2>
                                    <button onClick={() => setSelectedAgent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={20} color="var(--zoho-text-secondary)" /></button>
                                </div>

                                <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto' }}>

                                    {/* --- OVERVIEW TAB --- */}
                                    {agentModalTab === 'overview' && (() => {
                                        const agentPScore = Number(selectedAgent.productivityScore ?? 0);
                                        const agentRScore = Number(selectedAgent.riskScore ?? 0);
                                        const agentRLevel = selectedAgent.riskLevel || 'low';
                                        const riskColor = agentRLevel === 'critical' ? '#ff5630' : agentRLevel === 'high' ? '#ff8b00' : agentRLevel === 'medium' ? '#ffab00' : '#36b37e';
                                        return (
                                            <div>
                                                {/* Stats row */}
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
                                                    {[
                                                        { label: 'Team', value: selectedAgent.departmentName || 'Unassigned', color: 'var(--zoho-text-primary)' },
                                                        { label: 'Productivity %', value: `${Number(selectedAgent.productivityPct ?? 0)}%`, color: (selectedAgent.productivityPct || 0) >= 70 ? '#36b37e' : (selectedAgent.productivityPct || 0) >= 40 ? '#ffab00' : '#ff5630' },
                                                        { label: 'Productivity Score', value: `${agentPScore}/100`, color: agentPScore >= 70 ? '#36b37e' : agentPScore >= 40 ? '#ffab00' : '#ff5630' },
                                                        { label: 'Time Worked', value: formatTime(getEmployeeWorkedMs(selectedAgent)), color: '#0052cc' },
                                                        { label: 'Risk Level', value: agentRScore > 0 ? `${agentRLevel.toUpperCase()} (${agentRScore})` : 'CLEAN', color: riskColor },
                                                    ].map((s, i) => (
                                                        <div key={i} style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '14px 16px', border: '1px solid var(--zoho-border)' }}>
                                                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--zoho-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>{s.label}</div>
                                                            <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
                                                    {[
                                                        { label: 'Productive Time', value: formatTime(selectedAgent.productiveTimeMs), color: '#36b37e' },
                                                        { label: 'Non-Productive Time', value: formatTime(selectedAgent.nonProductiveTimeMs), color: '#ff5630' },
                                                        { label: 'Idle Excluded', value: formatTime(selectedAgent.idleExcludedMs), color: '#ffab00' },
                                                    ].map((s, i) => (
                                                        <div key={i} style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '14px 16px', border: '1px solid var(--zoho-border)' }}>
                                                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--zoho-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>{s.label}</div>
                                                            <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Productivity Score Breakdown */}
                                                {selectedAgent.productivityBreakdown && (
                                                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '16px 20px', marginBottom: 20, border: '1px solid var(--zoho-border)' }}>
                                                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--zoho-text-secondary)', textTransform: 'uppercase', marginBottom: 12 }}>Productivity Score Breakdown</div>
                                                        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                                                            <div style={{ width: 56, height: 56, borderRadius: '50%', border: `3px solid ${agentPScore >= 70 ? '#36b37e' : agentPScore >= 40 ? '#ffab00' : '#ff5630'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                <span style={{ fontSize: 18, fontWeight: 800, color: agentPScore >= 70 ? '#36b37e' : agentPScore >= 40 ? '#ffab00' : '#ff5630' }}>{agentPScore}</span>
                                                            </div>
                                                            <div style={{ flex: 1, fontSize: 12, color: 'var(--zoho-text-secondary)', lineHeight: 1.7 }}>
                                                                <div>Base Score: <strong style={{ color: 'var(--zoho-text-primary)' }}>{selectedAgent.productivityBreakdown.baseScore}</strong> (productive / total evaluated)</div>
                                                                <div>Penalty: <strong style={{ color: '#ff5630' }}>-{selectedAgent.productivityBreakdown.penalty}</strong> (non-productive time)</div>
                                                                <div>Idle time of <strong>{formatTime(selectedAgent.productivityBreakdown.idleExcludedMs)}</strong> excluded from calculation</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Risk Signals */}
                                                {selectedAgent.riskSignals && selectedAgent.riskSignals.length > 0 && (
                                                    <div style={{ background: '#fff0ec', borderRadius: 8, padding: '16px 20px', marginBottom: 20, border: '1px solid rgba(255,86,48,0.2)' }}>
                                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#bf2600', textTransform: 'uppercase', marginBottom: 10 }}>Risk Signals Detected</div>
                                                        {selectedAgent.riskSignals.map((sig, idx) => (
                                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: idx > 0 ? '1px solid rgba(255,86,48,0.1)' : 'none' }}>
                                                                <Shield size={13} color={sig.severity === 'critical' ? '#ff5630' : '#ffab00'} />
                                                                <span style={{ fontSize: 12, color: '#bf2600', flex: 1 }}>{sig.description}</span>
                                                                <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 3, background: sig.severity === 'critical' ? '#ff5630' : sig.severity === 'high' ? '#ffab00' : '#00b8d9', color: 'white' }}>
                                                                    {sig.severity.toUpperCase()}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Shift benchmark bar */}
                                                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '16px 20px', marginBottom: 20, border: '1px solid var(--zoho-border)' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                                        <span style={{ fontSize: 12, fontWeight: 600 }}>Shift Progress vs Expected</span>
                                                        <span style={{ fontSize: 12, color: 'var(--zoho-text-secondary)' }}>{summaryShiftStart} – {summaryShiftEnd}</span>
                                                    </div>
                                                    <div style={{ height: 8, background: 'rgba(255,255,255,0.10)', borderRadius: 4, overflow: 'hidden' }}>
                                                        <div style={{ height: '100%', width: `${Math.min(Number(selectedAgent.utilization || 0), 110)}%`, background: ws.color, borderRadius: 4, transition: 'width 0.5s' }}></div>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--zoho-text-secondary)' }}>
                                                        <span>0%</span><span style={{ color: '#0052cc' }}>100% (target)</span><span>110%+</span>
                                                    </div>
                                                </div>

                                                {/* System info */}
                                                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '16px 20px', border: '1px solid var(--zoho-border)' }}>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--zoho-text-secondary)', textTransform: 'uppercase', marginBottom: 12 }}>System Identification</div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                                        {[
                                                            { label: 'Device ID', value: selectedAgent.agentId?.slice(0, 20) + '...' },
                                                            { label: 'Hostname', value: selectedAgent.details?.hostname || '—' },
                                                            { label: 'OS', value: selectedAgent.details?.os || '—' },
                                                            { label: 'IP', value: selectedAgent.details?.ip || '—' },
                                                            { label: 'Last Seen', value: formatDateTime(selectedAgent.details?.lastSeen) },
                                                            { label: 'MAC', value: selectedAgent.details?.mac || '—' },
                                                        ].map((r, i) => (
                                                            <div key={i}>
                                                                <div style={{ fontSize: 10, color: 'var(--zoho-text-secondary)', fontWeight: 700 }}>{r.label}</div>
                                                                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--zoho-text-primary)', marginTop: 2 }}>{r.value}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* --- ATTENDANCE TAB --- */}
                                    {agentModalTab === 'attendance' && (
                                        <div>
                                            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                                                {[
                                                    { label: 'Days Present', value: (selectedAgent.attendance || []).length, color: '#36b37e' },
                                                    { label: 'Utilization %', value: `${selectedAgent.utilization || 0}%`, color: '#0052cc' },
                                                    { label: 'Worked Time', value: formatTime(selectedAgent.workedMs), color: '#0052cc' },
                                                ].map((s, i) => (
                                                    <div key={i} style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '14px 16px' }}>
                                                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--zoho-text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>{s.label}</div>
                                                        <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Monthly Calendar */}
                                            <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '16px 20px', marginBottom: 16, border: '1px solid var(--zoho-border)' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                                    <div style={{ fontWeight: 700, fontSize: 14 }}>Attendance Calendar</div>

                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                        <button
                                                            onClick={() => {
                                                                if (agentCalMonth === 0) { setAgentCalYear(agentCalYear - 1); setAgentCalMonth(11); }
                                                                else setAgentCalMonth(agentCalMonth - 1);
                                                            }}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--zoho-text-secondary)' }}
                                                        >
                                                            <ChevronLeft size={18} />
                                                        </button>
                                                        <span style={{ fontSize: 13, fontWeight: 600, minWidth: 100, textAlign: 'center' }}>
                                                            {new Date(agentCalYear, agentCalMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                if (agentCalMonth === 11) { setAgentCalYear(agentCalYear + 1); setAgentCalMonth(0); }
                                                                else setAgentCalMonth(agentCalMonth + 1);
                                                            }}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--zoho-text-secondary)' }}
                                                        >
                                                            <ChevronR size={18} />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
                                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                                                        <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--zoho-text-secondary)', padding: '4px 0' }}>{d}</div>
                                                    ))}
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                                                    {(() => {
                                                        const firstDay = new Date(agentCalYear, agentCalMonth, 1).getDay();
                                                        const daysInMonth = new Date(agentCalYear, agentCalMonth + 1, 0).getDate();
                                                        const now = new Date();
                                                        const localToday = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
                                                        const cells = [];

                                                        // Get tenant working days and holidays
                                                        const workingDays = data?.summary?.workingDays || [1, 2, 3, 4, 5];
                                                        const holidays = data?.summary?.holidays || [];

                                                        for (let i = 0; i < firstDay; i++) cells.push(<div key={`empty-${i}`} />);
                                                        for (let d = 1; d <= daysInMonth; d++) {
                                                            const date = new Date(agentCalYear, agentCalMonth, d);
                                                            const dStr = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
                                                            const isFuture = dStr > localToday;
                                                            const isWeekend = !workingDays.includes(date.getDay());
                                                            const holiday = holidays.find(h => h.date === dStr);
                                                            const att = (selectedAgent.attendance || []).find(a => a.date === dStr);

                                                            let bgColor = 'transparent';
                                                            let borderColor = 'var(--zoho-border)';
                                                            let borderStyle = isFuture ? 'dashed' : 'solid';
                                                            let textColor = 'var(--zoho-text-muted)';
                                                            let marker = null;

                                                            if (holiday) {
                                                                bgColor = 'rgba(255, 171, 0, 0.1)';
                                                                borderColor = 'rgba(255, 171, 0, 0.3)';
                                                                textColor = '#ffab00';
                                                            } else if (isWeekend) {
                                                                bgColor = 'repeating-linear-gradient(-45deg, rgba(200, 200, 200, 0.03), rgba(200, 200, 200, 0.03) 2px, rgba(0, 0, 0, 0.6) 2px, rgba(0, 0, 0, 0.6) 4px)';
                                                                borderColor = 'rgba(255, 255, 255, 0.1)';
                                                                textColor = '#777777';
                                                            } else if (att) {
                                                                bgColor = 'rgba(54, 179, 126, 0.15)';
                                                                borderColor = 'rgba(54, 179, 126, 0.4)';
                                                                textColor = '#36b37e';
                                                                marker = <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#36b37e', marginTop: 2 }} />;
                                                            } else if (!isFuture) {
                                                                bgColor = 'rgba(255, 86, 48, 0.1)';
                                                                borderColor = 'rgba(255, 86, 48, 0.2)';
                                                                textColor = '#ff5630';
                                                            }

                                                            cells.push(
                                                                <div key={d} title={holiday ? `Holiday: ${holiday.name}` : att ? `Present: ${new Date(att.checkInTime).toLocaleTimeString()} via ${att.triggeredBy}` : ''}
                                                                    style={{
                                                                        aspectRatio: '1', borderRadius: 6, background: bgColor, border: `1px ${borderStyle} ${borderColor}`,
                                                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                                                        position: 'relative', opacity: isFuture ? 0.4 : 1
                                                                    }}>
                                                                    <span style={{ fontSize: 12, fontWeight: 700, color: textColor }}>{isWeekend ? 'L' : d}</span>
                                                                    {marker}
                                                                </div>
                                                            );
                                                        }
                                                        return cells;
                                                    })()}
                                                </div>

                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 16, fontSize: 10, color: 'var(--zoho-text-secondary)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(54, 179, 126, 0.2)', border: '1px solid rgba(54,179,126,0.4)' }}></div> Present</div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(255, 86, 48, 0.1)', border: '1px solid rgba(255, 86, 48, 0.2)' }}></div> Absent</div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(255, 171, 0, 0.1)', border: '1px solid rgba(255, 171, 0, 0.3)' }}></div> Holiday</div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: 'repeating-linear-gradient(-45deg, rgba(200, 200, 200, 0.03), rgba(200, 200, 200, 0.03) 2px, rgba(0, 0, 0, 0.6) 2px, rgba(0, 0, 0, 0.6) 4px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}></div> Weekend/Rest</div>
                                                </div>
                                            </div>

                                            {/* Day-by-day table */}
                                            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                                                <table className="zoho-table">
                                                    <thead><tr><th>Date</th><th>Start Time</th><th>Start Source</th><th>Time Worked</th><th>Status</th></tr></thead>
                                                    <tbody>
                                                        {[...attendanceDays].reverse().map((d, i) => (
                                                            <tr key={i}>
                                                                <td style={{ fontSize: 12 }}>
                                                                    <div style={{ fontWeight: 600 }}>{d.date}</div>
                                                                    <div style={{ color: 'var(--zoho-text-secondary)', fontSize: 11 }}>{d.day}</div>
                                                                </td>
                                                                <td style={{ fontSize: 12, fontWeight: 600 }}>{d.checkIn}</td>
                                                                <td style={{ fontSize: 11, color: 'var(--zoho-text-secondary)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.triggeredBy}>{d.triggeredBy}</td>
                                                                <td style={{ fontSize: 12, fontWeight: 600 }}>{formatTime(d.ms)}</td>
                                                                <td>
                                                                    <span style={{
                                                                        padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                                                                        color: d.ms === 0 ? '#ff5630' : d.pct > 1 ? '#ff5630' : d.pct >= 0.8 ? '#36b37e' : '#ffab00',
                                                                        background: d.ms === 0 ? '#fff0ec' : d.pct > 1 ? '#fff0ec' : d.pct >= 0.8 ? '#e3fcef' : '#fffae5'
                                                                    }}>
                                                                        {d.ms === 0 ? 'No Work Logged' : d.pct > 1 ? 'Overtime' : d.pct >= 0.8 ? 'On Track' : 'Needs Attention'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* --- APPLICATIONS TAB --- */}
                                    {agentModalTab === 'apps' && (() => {
                                        const allApps = selectedAgent.topApps || [];
                                        const filteredApps = allApps.filter((app) => {
                                            if (modalAppTagFilter === 'all') return true;
                                            return (app.productivityType === 'NON_PRODUCTIVE' ? 'NON_PRODUCTIVE' : 'PRODUCTIVE') === modalAppTagFilter;
                                        });
                                        const totalMs = allApps.reduce((s, a) => s + a.durationMs, 0);
                                        const totalPages = Math.max(1, Math.ceil(filteredApps.length / MODAL_LIST_PAGE_SIZE));
                                        const page = Math.min(appsPage, totalPages);
                                        const pagedApps = filteredApps.slice((page - 1) * MODAL_LIST_PAGE_SIZE, page * MODAL_LIST_PAGE_SIZE);
                                        return (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                                                <div style={{ fontSize: 13, color: 'var(--zoho-text-secondary)' }}>Apps used in this period, ranked by time spent.</div>
                                                <div className="websites-filter-bar" style={{ padding: 0, border: 'none', background: 'transparent' }}>
                                                    {[{ key: 'all', label: 'All' }, { key: 'PRODUCTIVE', label: 'Productive' }, { key: 'NON_PRODUCTIVE', label: 'Non-Productive' }].map(({ key, label }) => (
                                                        <button key={key} onClick={() => { setModalAppTagFilter(key); setAppsPage(1); }}
                                                            className={`websites-filter-btn${modalAppTagFilter === key ? ' active' : ''}`}>
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {pagedApps.length > 0 ? (
                                                <>
                                                <div className="analytics-list-container" style={{ border: '1px solid var(--zoho-border)', borderRadius: 8, overflow: 'hidden' }}>
                                                    {pagedApps.map((app, i) => {
                                                        const pct = totalMs > 0 ? ((app.durationMs / totalMs) * 100) : 0;
                                                        const pType = app.productivityType === 'NON_PRODUCTIVE' ? 'NON_PRODUCTIVE' : 'PRODUCTIVE';
                                                        const pTypeColor = pType === 'NON_PRODUCTIVE' ? '#ff5630' : '#36b37e';
                                                        const pTypeBg = pType === 'NON_PRODUCTIVE' ? 'rgba(255,86,48,0.12)' : 'rgba(54,179,126,0.12)';
                                                        const pTypeLabel = pType === 'NON_PRODUCTIVE' ? 'Non-Productive' : 'Productive';
                                                        return (
                                                            <div key={i} className="analytics-list-row">
                                                                <span className="analytics-list-rank">{(page - 1) * MODAL_LIST_PAGE_SIZE + i + 1}</span>
                                                                <div className="analytics-list-icon" style={{ background: pTypeBg, border: `1px solid ${pTypeColor}30`, color: pTypeColor }}>
                                                                    {(app.appName || '?').charAt(0).toUpperCase()}
                                                                </div>
                                                                <div className="analytics-list-info">
                                                                    <div className="analytics-list-name">{app.appName}</div>
                                                                    <div className="analytics-list-progress">
                                                                        <div style={{ height: '100%', width: `${pct.toFixed(1)}%`, background: pTypeColor, borderRadius: 2, transition: 'width 0.4s' }} />
                                                                    </div>
                                                                </div>
                                                                <span className="analytics-list-time">{formatTime(app.durationMs)}</span>
                                                                <span className="analytics-list-meta">{app.category || '—'}</span>
                                                                <span className="analytics-list-badge" style={{ background: pTypeBg, color: pTypeColor, border: `1px solid ${pTypeColor}40` }}>{pTypeLabel}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {totalPages > 1 && (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 14 }}>
                                                        <button onClick={() => setAppsPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ background: 'transparent', border: '1px solid var(--zoho-border)', borderRadius: 6, padding: 4, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.4 : 1, display: 'flex' }}>
                                                            <ChevronLeft size={14} color="var(--zoho-text-secondary)" />
                                                        </button>
                                                        <span style={{ fontSize: 12, color: 'var(--zoho-text-secondary)' }}>{page} / {totalPages}</span>
                                                        <button onClick={() => setAppsPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ background: 'transparent', border: '1px solid var(--zoho-border)', borderRadius: 6, padding: 4, cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.4 : 1, display: 'flex' }}>
                                                            <ChevronRight size={14} color="var(--zoho-text-secondary)" />
                                                        </button>
                                                    </div>
                                                )}
                                                </>
                                            ) : (
                                                <div style={{ textAlign: 'center', padding: 40, color: 'var(--zoho-text-secondary)' }}>No application data for this period.</div>
                                            )}
                                        </div>
                                        );
                                    })()}

                                    {/* --- FILE ACTIVITY TAB --- */}
                                    {agentModalTab === 'web' && (() => {
                                        const allUrls = selectedAgent.topUrls || [];
                                        const filteredUrls = allUrls.filter((u) => {
                                            if (modalWebsiteTagFilter === 'all') return true;
                                            return (u.productivityTag || 'Productive') === modalWebsiteTagFilter;
                                        });
                                        const totalUrlMs = allUrls.reduce((s, u) => s + (u.durationMs || 0), 0);
                                        const totalPages = Math.max(1, Math.ceil(filteredUrls.length / MODAL_LIST_PAGE_SIZE));
                                        const page = Math.min(urlsPage, totalPages);
                                        const pagedUrls = filteredUrls.slice((page - 1) * MODAL_LIST_PAGE_SIZE, page * MODAL_LIST_PAGE_SIZE);
                                        return (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                                                <div style={{ fontSize: 13, color: 'var(--zoho-text-secondary)' }}>Top websites visited in this period.</div>
                                                <div className="websites-filter-bar" style={{ padding: 0, border: 'none', background: 'transparent' }}>
                                                    {[{ key: 'all', label: 'All' }, { key: 'Productive', label: 'Productive' }, { key: 'Unproductive', label: 'Non-Productive' }].map(({ key, label }) => (
                                                        <button key={key} onClick={() => { setModalWebsiteTagFilter(key); setUrlsPage(1); }}
                                                            className={`websites-filter-btn${modalWebsiteTagFilter === key ? ' active' : ''}`}>
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {pagedUrls.length > 0 ? (
                                                <>
                                                <div className="analytics-list-container" style={{ border: '1px solid var(--zoho-border)', borderRadius: 8, overflow: 'hidden' }}>
                                                    {pagedUrls.map((u, i) => {
                                                        const isNonWork = u.productivityTag === 'Unproductive';
                                                        const tagColor = isNonWork ? '#ff5630' : '#36b37e';
                                                        const tagBg = isNonWork ? 'rgba(255,86,48,0.12)' : 'rgba(54,179,126,0.12)';
                                                        const pct = totalUrlMs > 0 ? ((u.durationMs || 0) / totalUrlMs) * 100 : 0;
                                                        return (
                                                            <div key={i} className="analytics-list-row">
                                                                <span className="analytics-list-rank">{(page - 1) * MODAL_LIST_PAGE_SIZE + i + 1}</span>
                                                                <div className="analytics-list-icon" style={{ background: tagBg, border: `1px solid ${tagColor}30` }}>
                                                                    <Globe size={13} color={tagColor} />
                                                                </div>
                                                                <div className="analytics-list-info">
                                                                    <div className="analytics-list-name">{u.domain || '—'}</div>
                                                                    <div className="analytics-list-progress">
                                                                        <div style={{ height: '100%', width: `${pct.toFixed(1)}%`, background: tagColor, borderRadius: 2, transition: 'width 0.4s' }} />
                                                                    </div>
                                                                </div>
                                                                <span className="analytics-list-time">{formatTime(u.durationMs)}</span>
                                                                <span className="analytics-list-meta">{u.visits ?? 0} visit{(u.visits ?? 0) !== 1 ? 's' : ''}</span>
                                                                <span className="analytics-list-badge" style={{ background: tagBg, color: tagColor, border: `1px solid ${tagColor}40` }}>{getWebsiteTagLabel(u.productivityTag)}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {totalPages > 1 && (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 14 }}>
                                                        <button onClick={() => setUrlsPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ background: 'transparent', border: '1px solid var(--zoho-border)', borderRadius: 6, padding: 4, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.4 : 1, display: 'flex' }}>
                                                            <ChevronLeft size={14} color="var(--zoho-text-secondary)" />
                                                        </button>
                                                        <span style={{ fontSize: 12, color: 'var(--zoho-text-secondary)' }}>{page} / {totalPages}</span>
                                                        <button onClick={() => setUrlsPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ background: 'transparent', border: '1px solid var(--zoho-border)', borderRadius: 6, padding: 4, cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.4 : 1, display: 'flex' }}>
                                                            <ChevronRight size={14} color="var(--zoho-text-secondary)" />
                                                        </button>
                                                    </div>
                                                )}
                                                </>
                                            ) : (
                                                <div style={{ textAlign: 'center', padding: 40, color: 'var(--zoho-text-secondary)' }}>No web activity recorded for this period.</div>
                                            )}
                                        </div>
                                        );
                                    })()}

                                    {agentModalTab === 'uploads' && (() => {
                                        const uploadsTotalPages = Math.max(1, Math.ceil((selectedAgent.files?.length || 0) / MODAL_LIST_PAGE_SIZE));
                                        const uPage = Math.min(uploadsPage, uploadsTotalPages);
                                        const pagedUploads = (selectedAgent.files || []).slice((uPage - 1) * MODAL_LIST_PAGE_SIZE, uPage * MODAL_LIST_PAGE_SIZE);
                                        return (
                                        <div>
                                            <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--zoho-text-secondary)' }}>Files uploaded in this period.</div>
                                            {pagedUploads.length > 0 ? (
                                                <>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                    {pagedUploads.map((file, i) => (
                                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'rgba(255,255,255,0.06)', borderRadius: 8, border: '1px solid var(--zoho-border)' }}>
                                                            <FileText size={20} color="#0052cc" />
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--zoho-text-primary)' }}>{file.name}</div>
                                                                <div style={{ fontSize: 11, color: 'var(--zoho-text-secondary)', marginTop: 2 }}>{file.dest || 'Local action'}</div>
                                                            </div>
                                                            <div style={{ fontSize: 11, color: 'var(--zoho-text-muted)', whiteSpace: 'nowrap' }}>{formatDateTime(file.time)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {uploadsTotalPages > 1 && (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 14 }}>
                                                        <button onClick={() => setUploadsPage(p => Math.max(1, p - 1))} disabled={uPage <= 1} style={{ background: 'transparent', border: '1px solid var(--zoho-border)', borderRadius: 6, padding: 4, cursor: uPage <= 1 ? 'not-allowed' : 'pointer', opacity: uPage <= 1 ? 0.4 : 1, display: 'flex' }}>
                                                            <ChevronLeft size={14} color="var(--zoho-text-secondary)" />
                                                        </button>
                                                        <span style={{ fontSize: 12, color: 'var(--zoho-text-secondary)' }}>{uPage} / {uploadsTotalPages}</span>
                                                        <button onClick={() => setUploadsPage(p => Math.min(uploadsTotalPages, p + 1))} disabled={uPage >= uploadsTotalPages} style={{ background: 'transparent', border: '1px solid var(--zoho-border)', borderRadius: 6, padding: 4, cursor: uPage >= uploadsTotalPages ? 'not-allowed' : 'pointer', opacity: uPage >= uploadsTotalPages ? 0.4 : 1, display: 'flex' }}>
                                                            <ChevronRight size={14} color="var(--zoho-text-secondary)" />
                                                        </button>
                                                    </div>
                                                )}
                                                </>
                                            ) : (
                                                <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--zoho-text-secondary)' }}>
                                                    <FileText size={36} color="var(--zoho-text-faint)" style={{ marginBottom: 12 }} />
                                                    <div>No file uploads recorded for this period.</div>
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })()}

                                    {agentModalTab === 'changes' && (() => {
                                        const changeTypeStyle = {
                                            Rename: { icon: Edit3, color: '#0052cc', bg: '#e3f2fd' },
                                            Rewrite: { icon: RefreshCw, color: '#b45309', bg: '#fef3c7' },
                                            Delete: { icon: Trash2, color: '#bf2600', bg: '#fff0ec' },
                                        };
                                        const changesTotalPages = Math.max(1, Math.ceil((selectedAgent.fileChanges?.length || 0) / MODAL_LIST_PAGE_SIZE));
                                        const cPage = Math.min(changesPage, changesTotalPages);
                                        const pagedChanges = (selectedAgent.fileChanges || []).slice((cPage - 1) * MODAL_LIST_PAGE_SIZE, cPage * MODAL_LIST_PAGE_SIZE);
                                        return (
                                        <div>
                                            <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--zoho-text-secondary)' }}>File renames, rewrites, and deletes captured by the monitoring service.</div>
                                            {pagedChanges.length > 0 ? (
                                                <>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                    {pagedChanges.map((change, i) => {
                                                        const style = changeTypeStyle[change.type] || changeTypeStyle.Rename;
                                                        const Icon = style.icon;
                                                        return (
                                                            <div key={i} style={{ padding: '12px 16px', background: 'rgba(255,255,255,0.06)', borderRadius: 8, border: '1px solid var(--zoho-border)' }}>
                                                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, minWidth: 0 }}>
                                                                        <Icon size={16} color={style.color} />
                                                                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--zoho-text-primary)' }}>{change.name || 'Unknown file'}</span>
                                                                        <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: style.bg, color: style.color }}>{change.type}</span>
                                                                    </div>
                                                                    <div style={{ fontSize: 11, color: 'var(--zoho-text-muted)', whiteSpace: 'nowrap' }}>{formatDateTime(change.time)}</div>
                                                                </div>
                                                                {change.description && (
                                                                    <div style={{ fontSize: 11, color: 'var(--zoho-text-secondary)', marginTop: 6 }}>{change.description}</div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {changesTotalPages > 1 && (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 14 }}>
                                                        <button onClick={() => setChangesPage(p => Math.max(1, p - 1))} disabled={cPage <= 1} style={{ background: 'transparent', border: '1px solid var(--zoho-border)', borderRadius: 6, padding: 4, cursor: cPage <= 1 ? 'not-allowed' : 'pointer', opacity: cPage <= 1 ? 0.4 : 1, display: 'flex' }}>
                                                            <ChevronLeft size={14} color="var(--zoho-text-secondary)" />
                                                        </button>
                                                        <span style={{ fontSize: 12, color: 'var(--zoho-text-secondary)' }}>{cPage} / {changesTotalPages}</span>
                                                        <button onClick={() => setChangesPage(p => Math.min(changesTotalPages, p + 1))} disabled={cPage >= changesTotalPages} style={{ background: 'transparent', border: '1px solid var(--zoho-border)', borderRadius: 6, padding: 4, cursor: cPage >= changesTotalPages ? 'not-allowed' : 'pointer', opacity: cPage >= changesTotalPages ? 0.4 : 1, display: 'flex' }}>
                                                            <ChevronRight size={14} color="var(--zoho-text-secondary)" />
                                                        </button>
                                                    </div>
                                                )}
                                                </>
                                            ) : (
                                                <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--zoho-text-secondary)' }}>
                                                    <FileText size={36} color="var(--zoho-text-faint)" style={{ marginBottom: 12 }} />
                                                    <div>No file changes recorded for this period.</div>
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })()}

                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

// --- Sub-components ---

const AgentMultiSelect = ({ allAgents, selectedAgentIds, onSave, onCancel }) => {
    const [selected, setSelected] = useState(new Set(selectedAgentIds));

    const toggleAgent = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid var(--zoho-border)', borderRadius: 4 }}>
                <table className="zoho-table">
                    <thead>
                        <tr>
                            <th style={{ width: 40 }}></th>
                            <th>Team Member</th>
                            <th>Device Name</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allAgents.map((agent, i) => (
                            <tr key={i} onClick={() => toggleAgent(agent.agentId)} style={{ cursor: 'pointer' }}>
                                <td>
                                    <input type="checkbox" checked={selected.has(agent.agentId)} readOnly />
                                </td>
                                <td>
                                    <div style={{ fontWeight: 600 }}>{displayName(agent)}</div>
                                </td>
                                <td>{agent.details?.hostname || "Unknown"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn-zoho" onClick={onCancel}>Cancel</button>
                <button className="btn-zoho btn-zoho-primary" onClick={() => onSave(Array.from(selected))}>Save Assignments</button>
            </div>
        </div>
    );
};

const TrendAreaChart = ({ data }) => {
    const [hoverIndex, setHoverIndex] = useState(null);
    if (!data || data.length === 0) return <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--zoho-text-faint)' }}>No activity data found</div>;

    const height = 240;
    const width = 500;
    const padding = { top: 16, right: 16, bottom: 44, left: 16 };
    const innerWidth = Math.max(1, width - padding.left - padding.right);
    const innerHeight = Math.max(1, height - padding.top - padding.bottom);

    const formatUsage = (ms) => {
        const safeMs = Math.max(0, ms || 0);
        const totalMins = Math.round(safeMs / 60000);
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        if (h === 0) return `${m}m`;
        if (m === 0) return `${h}h`;
        return `${h}h ${m}m`;
    };

    const maxVal = Math.max(...data.map(d => d.usage)) || 1;
    const minVal = 0;

    const denom = Math.max(1, data.length - 1);
    const getX = (i) => padding.left + (i / denom) * innerWidth;
    const getY = (val) => (padding.top + innerHeight) - ((val - minVal) / (maxVal - minVal)) * innerHeight;

    let pathD = `M ${getX(0)} ${getY(data[0].usage)}`;
    data.forEach((d, i) => { pathD += ` L ${getX(i)} ${getY(d.usage)}`; });

    const baseY = padding.top + innerHeight;
    const areaD = `${pathD} L ${getX(data.length - 1)} ${baseY} L ${getX(0)} ${baseY} Z`;

    const tickCount = Math.min(6, data.length);
    const tickStep = tickCount <= 1 ? 1 : Math.ceil((data.length - 1) / (tickCount - 1));
    const tickIndexes = [];
    for (let i = 0; i < data.length; i += tickStep) tickIndexes.push(i);
    if (tickIndexes[tickIndexes.length - 1] !== data.length - 1) tickIndexes.push(data.length - 1);

    const yTicks = [0, maxVal * 0.5, maxVal];

    const handleMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const relX = e.clientX - rect.left;
        const t = rect.width > 0 ? relX / rect.width : 0;
        const viewX = t * width;
        const idx = Math.min(
            data.length - 1,
            Math.max(0, Math.round(((viewX - padding.left) / innerWidth) * denom))
        );
        setHoverIndex(idx);
    };

    const activePoint = hoverIndex != null ? data[hoverIndex] : null;
    const activeX = hoverIndex != null ? getX(hoverIndex) : null;
    const activeY = hoverIndex != null ? getY(activePoint?.usage || 0) : null;
    const activeXPct = activeX != null ? (activeX / width) * 100 : 0;
    const activeYPct = activeY != null ? (activeY / height) * 100 : 0;

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {activePoint && (
                <div
                    style={{
                        position: 'absolute',
                        left: `${activeXPct}%`,
                        top: `${activeYPct}%`,
                        transform: 'translate(-50%, -110%)',
                        background: 'rgba(9, 30, 66, 0.92)',
                        color: 'white',
                        padding: '6px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap'
                    }}
                >
                    <div style={{ fontWeight: 700 }}>{activePoint.label || `#${hoverIndex + 1}`}</div>
                    <div style={{ opacity: 0.9 }}>{formatUsage(activePoint.usage)} total</div>
                </div>
            )}

            <svg
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="none"
                style={{ width: '100%', height: '100%' }}
                onMouseMove={handleMove}
                onMouseLeave={() => setHoverIndex(null)}
            >
                <defs>
                    <linearGradient id="zohoTrendGradient" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="var(--zoho-accent)" stopOpacity="0.34" />
                        <stop offset="100%" stopColor="var(--zoho-accent)" stopOpacity="0" />
                    </linearGradient>
                </defs>

                {yTicks.map((v, i) => (
                    <g key={i}>
                        <line x1={padding.left} y1={getY(v)} x2={width - padding.right} y2={getY(v)} stroke="rgba(255,255,255,0.10)" />
                        <text x={padding.left} y={getY(v) - 4} fontSize="10" fill="var(--zoho-text-muted)">{formatUsage(v)}</text>
                    </g>
                ))}

                <line x1={padding.left} y1={baseY} x2={width - padding.right} y2={baseY} stroke="var(--zoho-border)" />
                {tickIndexes.map((i) => (
                    <g key={i}>
                        <line x1={getX(i)} y1={baseY} x2={getX(i)} y2={baseY + 4} stroke="var(--zoho-text-faint)" />
                        <text x={getX(i)} y={height - 14} textAnchor="middle" fontSize="10" fill="var(--zoho-text-secondary)">
                            {data[i]?.label || ""}
                        </text>
                    </g>
                ))}

                <path d={areaD} fill="url(#zohoTrendGradient)" />
                <path d={pathD} fill="none" stroke="var(--zoho-accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                {data.length < 40 && data.map((d, i) => (
                    <circle key={i} cx={getX(i)} cy={getY(d.usage)} r={hoverIndex === i ? 4 : 3} fill="var(--zoho-accent)" opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.55} />
                ))}
                {/* Emphasized endpoint — the most recent value is the one that matters most */}
                <circle
                    cx={getX(data.length - 1)}
                    cy={getY(data[data.length - 1].usage)}
                    r="4.5"
                    fill="var(--zoho-accent)"
                    stroke="var(--zoho-card-bg)"
                    strokeWidth="2"
                />

                {hoverIndex != null && (
                    <line x1={getX(hoverIndex)} y1={padding.top} x2={getX(hoverIndex)} y2={baseY} stroke="var(--zoho-accent)" strokeDasharray="3 3" opacity="0.35" />
                )}
            </svg>
        </div>
    );
};

// Fixed name → color map (never positional) so a slice's color stays tied to what
// it represents regardless of sort order or which slices are present.
const TIME_ALLOCATION_COLORS = {
    'Idle': '#8993a4',
    'Productive Apps': '#36b37e',
    'Productive Web': '#00b8d9',
    'Non-Productive Apps': '#ff5630',
    'Non-Productive Web': '#ff8b00',
};

const DeepWorkDonut = ({ summary }) => {
    const data = {
        'Idle': summary?.totalTrackedIdleMs || 0,
        'Productive Apps': summary?.appProductiveMs || 0,
        'Productive Web': summary?.webProductiveTimeMs || 0,
        'Non-Productive Apps': summary?.appNonProductiveMs || 0,
        'Non-Productive Web': summary?.webNonProductiveTimeMs || 0,
    };
    const getCategoryColor = (cat) => TIME_ALLOCATION_COLORS[cat] || '#8993a4';
    return <CategoryDonutChart data={data} getCategoryColor={getCategoryColor} />;
};

const CategoryDonutChart = ({ data, getCategoryColor }) => {
    const items = data ? Object.entries(data).filter(([_, val]) => val > 0).sort((a, b) => b[1] - a[1]) : [];
    // Guard on items.length, not Object.keys(data).length — data can have keys
    // (e.g. {Productive: 0, 'Non-Productive': 0}) whose values all filter out above,
    // leaving an empty items/total=0 that would otherwise crash the rounding logic below.
    if (items.length === 0) return <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--zoho-text-faint)' }}>Insignificant usage detected</div>;
    const total = items.reduce((sum, [_, val]) => sum + val, 0);
    let cumPercent = 0;

    // Largest-remainder rounding: independent Math.round() per slice can make the
    // displayed percentages total 99% or 101%. Round every slice down first, then
    // hand out the leftover points (based on largest fractional remainder) so the
    // displayed percentages always sum to exactly 100.
    const rawPercents = items.map(([, val]) => (val / total) * 100);
    const flooredPercents = rawPercents.map(Math.floor);
    const remainder = 100 - flooredPercents.reduce((sum, p) => sum + p, 0);
    const remainderOrder = rawPercents
        .map((p, i) => ({ i, frac: p - flooredPercents[i] }))
        .sort((a, b) => b.frac - a.frac);
    const displayPercents = [...flooredPercents];
    for (let k = 0; k < remainder; k++) {
        displayPercents[remainderOrder[k].i] += 1;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <div style={{ height: 120, display: 'flex', justifyContent: 'center' }}>
                <svg viewBox="0 0 40 40" style={{ height: '100%' }}>
                    {items.map(([cat, val]) => {
                        const percent = val / total;
                        const dashArray = `${percent * 100} ${100 - percent * 100}`;
                        const dashOffset = 25 - cumPercent * 100;
                        cumPercent += percent;
                        return (
                            <circle key={cat} r="15.9155" cx="20" cy="20" fill="transparent" stroke={getCategoryColor(cat)} strokeWidth="6" strokeDasharray={dashArray} strokeDashoffset={dashOffset} style={{ transformOrigin: 'center', transform: 'rotate(-90deg)' }} />
                        );
                    })}
                </svg>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.slice(0, 5).map(([cat, val], i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: getCategoryColor(cat) }}></span>
                        <span style={{ flex: 1, color: 'var(--zoho-text-secondary)' }}>{cat}</span>
                        <span style={{ fontWeight: 600 }}>{displayPercents[i]}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AnalyticsDashboard;





